// Resources hook for React Native admin app
// Based on admin-web resources implementation and mobile app patterns

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  useListResourcesQuery,
  useLazyListResourcesQuery,
  useCreateResourceMutation,
  useEditResourceMutation,
  useDeleteResourceMutation,
  useUpdateResourcePositionsMutation,
} from '@/store/api/admin/resourcesApi';
import { 
  ICommunityResource, 
  IResourceResponse,
  PRIMARY_POSITIONS,
  SECONDARY_POSITIONS,
  ROW_SIZE,
  NUM_PRIMARY_SLOTS,
} from '@/types/admin/resources';
import { adjustForEmptySlots } from '@/utils/admin/resourcesGrid';
import { useAdminUser } from './useAdminUser';
import { createEnhancedError } from '@/utils/admin/errorHandling';

export const useResources = () => {
  // Get admin user data (including selected property)
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  // Lazy query for manual triggering (matching admin-web exactly)
  const [getResources, { data: resourcesData, isLoading: isResourcesLoading, isUninitialized: isResourcesUninitialized, error: resourcesError }] = useLazyListResourcesQuery();

  // Local state
  const [resources, setResources] = useState<ICommunityResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<ICommunityResource | null>(null);
  const [lastSyncedData, setLastSyncedData] = useState<typeof resourcesData>(undefined);

  // Retry state
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Mutations
  const [createResource, { isLoading: isCreating }] = useCreateResourceMutation();
  const [editResource, { isLoading: isEditing }] = useEditResourceMutation();
  const [deleteResource, { isLoading: isDeleting }] = useDeleteResourceMutation();
  const [updateResourcePositions, { isLoading: isUpdatingPositions }] = useUpdateResourcePositionsMutation();

  // Enhanced error handling
  const enhancedError = useMemo(() => {
    if (!resourcesError) return null;
    return createEnhancedError(resourcesError as any, 'resources');
  }, [resourcesError]);

  // Trigger API call when propertyId changes (matching admin-web exactly)
  useEffect(() => {
    if (selectedPropertyId) {
      const searchParams = new URLSearchParams();
      searchParams.append("propertyId", selectedPropertyId.toString());
      getResources(searchParams);
    }
  }, [getResources, selectedPropertyId]);

  // Sync during render (not useEffect) so local state updates in the same commit RTK
  // exposes new data — avoids a one-frame empty-state flash after isLoading flips false.
  if (resourcesData && resourcesData !== lastSyncedData) {
    setLastSyncedData(resourcesData);
    if (resourcesData.response) {
      setResources(resourcesData.response);
    }
  }

  // Calculate highest position and number of slots needed
  const highestPosition = useMemo(() => {
    return resources.length
      ? Math.max(...resources.map((resource) => resource.position))
      : undefined;
  }, [resources]);

  const numSlots = useMemo(() => {
    // We use highestPosition + 2 here because it's zero indexed and we want a free space after the last position
    const base = (highestPosition ?? 0) + 2;
    const calculatedSlots = Math.ceil(base / ROW_SIZE) * ROW_SIZE;
    return calculatedSlots > 8 ? calculatedSlots : 8;
  }, [highestPosition]);

  // Generate organized resource data for display
  const organizedResources = useMemo(() => {
    const primaryResources = adjustForEmptySlots(resources, PRIMARY_POSITIONS);
    const secondaryResources = adjustForEmptySlots(resources, SECONDARY_POSITIONS(numSlots));
    
    return {
      primary: primaryResources,
      secondary: secondaryResources,
      all: [...primaryResources, ...secondaryResources],
    };
  }, [resources, numSlots]);

  // Generate resource tiles for grid display
  const generateResourceTiles = useMemo(() => {
    const tiles = [];
    for (let i = 0; i < numSlots; i++) {
      const resource = resources.find((r) => r.position === i);
      tiles.push({
        position: i,
        resource,
        isPrimary: i < NUM_PRIMARY_SLOTS,
      });
    }
    return tiles;
  }, [resources, numSlots]);

  // Persist positions helper
  const persistPositions = useCallback(async (ordered: ICommunityResource[]) => {
    const pairings = ordered.map((resource) => ({ resourceId: resource.id, position: resource.position }));
    const patchForm = new FormData();
    patchForm.append('resourceIdPositionPairings', JSON.stringify(pairings));
    await updateResourcePositions(patchForm).unwrap();
  }, [updateResourcePositions]);

  // Update resource positions (for drag and drop functionality)
  const handleUpdateResourcePositions = useCallback(async (
    resourceId: number, 
    targetPosition: number
  ) => {
    // Save snapshot for accurate revert on failure
    const previousResources = resources;
    const resourceToUpdate = resources.find(r => r.id === resourceId);
    if (!resourceToUpdate) return;

    // Complex position update logic (matching admin-web)
    const movingForward = resourceToUpdate.position < targetPosition;
    const switchingLists =
      (resourceToUpdate.position < 4 && targetPosition >= 4) ||
      (resourceToUpdate.position >= 4 && targetPosition < 4);
    
    let adjustedResources: ICommunityResource[] = [];

    if (movingForward) {
      resources.forEach((resource) => {
        // Assign the resource to the target position
        if (resource.id === resourceId) {
          adjustedResources.push({ ...resource, position: targetPosition });
        }
        if (switchingLists) {
          // If introducing a new element to the second list, push everything after target back 1
          if (resource.position >= targetPosition) {
            adjustedResources.push({
              ...resource,
              position: resource.position + 1,
            });
          }
        } else {
          // Pull the element at the target position back if we're not switching lists
          if (resource.position === targetPosition) {
            adjustedResources.push({
              ...resource,
              position: resource.position - 1,
            });
          }
        }
        // If between original and target, pull back if there's no gap behind
        if (
          resource.position > resourceToUpdate.position &&
          resource.position < targetPosition &&
          resources.find((r) => r.position === resource.position - 1)
        ) {
          adjustedResources.push({
            ...resource,
            position: resource.position - 1,
          });
        }
        // Add element if not already included
        if (!adjustedResources.find((r) => r.id === resource.id)) {
          adjustedResources.push(resource);
        }
      });
    }

    if (!movingForward) {
      resources.forEach((resource) => {
        // Assign the resource to the target position
        if (resource.id === resourceId) {
          adjustedResources.push({ ...resource, position: targetPosition });
        }
        if (switchingLists) {
          const lastPrimaryPosition = Math.max(
            ...resources
              .filter((r) => PRIMARY_POSITIONS.includes(r.position))
              .map((r) => r.position)
          );

          // Only adjust positions after original if there's no overflow from first list
          if (lastPrimaryPosition < 3 && resource.position > resourceToUpdate.position) {
            adjustedResources.push({
              ...resource,
              position: resource.position - 1,
            });
          }
        }
        // If between target and original, push forward if there's no gap behind
        if (
          resource.position >= targetPosition &&
          resource.position < resourceToUpdate.position &&
          (resources.find((r) => r.position === resource.position - 1) ||
            resource.position === 0 ||
            (!switchingLists && resource.position === 4))
        ) {
          adjustedResources.push({
            ...resource,
            position: resource.position + 1,
          });
        }
        // Add element if not already included
        if (!adjustedResources.find((r) => r.id === resource.id)) {
          adjustedResources.push(resource);
        }
      });
    }

    // Sort and adjust for empty slots
    const sortedResources = adjustedResources.sort((a, b) => a.position - b.position);
    const primaryResources = adjustForEmptySlots(sortedResources, PRIMARY_POSITIONS);
    const secondaryResources = adjustForEmptySlots(sortedResources, SECONDARY_POSITIONS(numSlots));
    const finalResources = [...primaryResources, ...secondaryResources];

    // Update local state
    setResources(finalResources);

    try {
      await persistPositions(finalResources);
    } catch (error) {
      console.error('Failed to update resource positions:', error);
      // Revert local state on error
      setResources(previousResources);
      throw error;
    }
  }, [resources, numSlots, persistPositions]);

  // Assign empty slot (simpler position update)
  const handleAssignEmptySlot = useCallback(async (
    resourceId: number, 
    position: number
  ) => {
    // Save snapshot for accurate revert on failure
    const previousResources = resources;
    const sortedResults = [...resources].sort((a, b) => a.position - b.position);
    const results = sortedResults
      .map((resource) => {
        if (resource.id === resourceId) {
          return { ...resource, position };
        }
        return resource;
      })
      .sort((a, b) => a.position - b.position);
    
    const primaryResources = adjustForEmptySlots(results, PRIMARY_POSITIONS);
    const secondaryResources = adjustForEmptySlots(results, SECONDARY_POSITIONS(numSlots));
    const combinedResources = [...primaryResources, ...secondaryResources];

    // Update local state
    setResources(combinedResources);

    try {
      await persistPositions(combinedResources);
    } catch (error) {
      console.error('Failed to assign empty slot:', error);
      // Revert local state on error
      setResources(previousResources);
      throw error;
    }
  }, [resources, numSlots, persistPositions]);

  // Manual retry functionality
  const canRetry = retryCount < MAX_RETRIES;
  const manualRetry = useCallback(async () => {
    if (canRetry && selectedPropertyId) {
      setRetryCount(prev => prev + 1);
      const searchParams = new URLSearchParams();
      searchParams.append("propertyId", selectedPropertyId.toString());
      await getResources(searchParams);
    }
  }, [canRetry, selectedPropertyId, getResources]);

  // Refresh data
  const refresh = useCallback(async () => {
    if (selectedPropertyId) {
      setRetryCount(0);
      const searchParams = new URLSearchParams();
      searchParams.append("propertyId", selectedPropertyId.toString());
      await getResources(searchParams);
    }
  }, [selectedPropertyId, getResources]);

  // Loading states
  const isLoading = isResourcesLoading || isResourcesUninitialized || isCreating || isEditing || isDeleting || isUpdatingPositions;

  return {
    // Data
    data: resourcesData || { success: false, response: [] },
    resources,
    organizedResources,
    selectedResource,
    highestPosition,
    numSlots,
    
    // Loading states
    isLoading,
    isCreating,
    isEditing,
    isDeleting,
    isUpdatingPositions,
    
    // Error handling
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    
    // Actions
    setSelectedResource,
    refresh,
    generateResourceTiles,
    handleUpdateResourcePositions,
    handleAssignEmptySlot,
    
    // Mutations
    createResource,
    editResource,
    deleteResource,
    updateResourcePositions,
    
    // Manual queries
    getResources,
  };
};
